import React, { useState } from 'react';
import { cva } from 'class-variance-authority';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Icon } from './Icon';
import { entranceSpring } from './primitives/motion';
import styles from './Tag.module.css';

// ── CVA ─────────────────────────────────────────────────────────────────────

const tag = cva(styles.base, {
  variants: {
    color: {
      default: styles.default,
      success: styles.success,
      warning: styles.warning,
      danger: styles.danger,
      accent: styles.accent,
    },
    bordered: { false: styles.unbordered },
  },
  defaultVariants: {
    color: 'default',
    bordered: true,
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type TagPresetColor = 'default' | 'success' | 'warning' | 'danger' | 'accent';
type TagColor = TagPresetColor | (string & {});

type SpanMotionSafeProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

interface TagProps extends Omit<SpanMotionSafeProps, 'color'> {
  /** Preset or custom color */
  color?: TagColor;
  /** Show close button */
  closable?: boolean;
  /** Called when close is clicked */
  onClose?: (e: React.MouseEvent) => void;
  /** Icon before the label */
  icon?: React.ReactNode;
  /** Show border */
  bordered?: boolean;
  children?: React.ReactNode;
}

const presetColors = new Set<TagPresetColor>(['default', 'success', 'warning', 'danger', 'accent']);

function isPreset(color: TagColor): color is TagPresetColor {
  return presetColors.has(color as TagPresetColor);
}

/**
 * Tag — label/category marker.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | color | 'default' \| 'success' \| 'warning' \| 'danger' \| 'accent' \| string | 'default' | Preset or custom color |
 * | closable | boolean | false | Show X button |
 * | onClose | (e) => void | — | Close handler |
 * | icon | ReactNode | — | Icon before label |
 * | bordered | boolean | true | Show border |
 *
 * @example
 * <Tag>Default</Tag>
 * <Tag color="success">Published</Tag>
 * <Tag color="danger" closable onClose={handleRemove}>Error</Tag>
 * <Tag icon={<Clock size={12} />}>Recent</Tag>
 * <Tag color="#7C3AED">Custom Color</Tag>
 *
 * Motion: Tag pops in on mount with the shared `entranceSpring` preset. To
 * get the exit animation when consumers remove tags from a list, wrap the
 * mapped output with `<AnimatePresence>` at the call site:
 *
 *     <AnimatePresence>
 *       {tags.map(t => <Tag key={t.id} closable onClose={() => remove(t.id)}>{t.label}</Tag>)}
 *     </AnimatePresence>
 *
 * Without AnimatePresence at the call site, only the entrance animation is
 * observable — React unmounts the Tag synchronously on removal.
 */
export function Tag({
  color = 'default',
  closable = false,
  onClose,
  icon,
  bordered = true,
  children,
  style,
  className,
  ...props
}: TagProps) {
  const safeProps = props;
  const [visible, setVisible] = useState(true);
  const shouldReduce = useReducedMotion() ?? false;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose?.(e);
    if (!onClose) setVisible(false);
  };

  // For custom colors, pass via CSS custom properties
  const customColorStyle = !isPreset(color)
    ? {
        '--tag-bg': `color-mix(in srgb, ${color} 12%, transparent)`,
        '--tag-text': color,
        '--tag-border': `color-mix(in srgb, ${color} 30%, transparent)`,
      } as React.CSSProperties
    : undefined;

  // Honor `prefers-reduced-motion` — omit the spring entirely when set.
  const motionProps = shouldReduce ? {} : entranceSpring;

  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          {...motionProps}
          className={tag({ color: isPreset(color) ? color : undefined, bordered, className })}
          // Custom color values — CSS custom properties passthrough
          style={{ ...customColorStyle, ...style } as React.CSSProperties}
          {...safeProps}
        >
          {icon && <span className={styles.icon}>{icon}</span>}
          {children}
          {closable && (
            <button
              type="button"
              onClick={handleClose}
              className={styles.closeBtn}
              title="Remove"
            >
              <Icon name="close" size={12} />
            </button>
          )}
        </motion.span>
      )}
    </AnimatePresence>
  );
}
